import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useAuth } from '~/apis/auth';
import { IApplicant, ICard, IPayment } from '../models';
import BaseApi from './base';

type IUpdateProfileRequest = IApplicant;

type IGetProfileResponse = IApplicant &
  Pick<IPayment, 'cards'> & {
    secondAppDisc: boolean;
  };

interface IGetNotiResponse {
  count: number;
}

interface ICreateCardRequest {
  card: ICard;
}

interface IDeleteCardRequest {
  paymentMethodId: string;
}

class HomeApiClass {
  getProfile(): Promise<AxiosResponse<IGetProfileResponse>> {
    return BaseApi.get<IGetProfileResponse>('/home/profile');
  }

  updateProfile(request: IUpdateProfileRequest): Promise<AxiosResponse<void>> {
    return BaseApi.post<void>('/home/profile/update', request);
  }

  getNoti(): Promise<AxiosResponse<IGetNotiResponse>> {
    return BaseApi.get<IGetNotiResponse>('/home/noti');
  }

  createCard(request: ICreateCardRequest): Promise<AxiosResponse<void>> {
    return BaseApi.post<void>('/home/profile/card', request);
  }

  deleteCard(request: IDeleteCardRequest): Promise<AxiosResponse<void>> {
    return BaseApi.post<void>('/home/profile/card/delete', request);
  }
}

const HomeApi = new HomeApiClass();

export default HomeApi;

const QueryKey = 'home';

export const useProfile = (): UseQueryResult<IGetProfileResponse, unknown> => {
  const { data: authData } = useAuth();

  return useQuery([QueryKey, 'profile', authData?.accessToken], async () => {
    const { data } = await HomeApi.getProfile();
    return data;
  });
};

export const useNoti = (enabled = false): UseQueryResult<IGetNotiResponse, unknown> => {
  return useQuery(
    [QueryKey, 'noti'],
    async () => {
      const { data } = await HomeApi.getNoti();
      return data;
    },
    {
      enabled,
    }
  );
};

export const useProfileMutation = (): UseMutationResult<
  AxiosResponse<void>,
  unknown,
  IUpdateProfileRequest,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation(HomeApi.updateProfile, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKey, 'profile']);
    },
  });
};

export const useCardCreateMutation = (): UseMutationResult<
  AxiosResponse<void>,
  unknown,
  ICreateCardRequest,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation(HomeApi.createCard, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKey, 'profile']);
    },
  });
};

export const useCardDeleteMutation = (): UseMutationResult<
  AxiosResponse<void>,
  unknown,
  IDeleteCardRequest,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation(HomeApi.deleteCard, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKey, 'profile']);
    },
  });
};
